import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Building2, Mail, Lock, Eye, EyeOff, AlertCircle, Globe } from 'lucide-react';

export default function Login() {
  const { login }  = useAuth();
  const { t, i18n } = useTranslation();
  const navigate   = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--ink)' }}>
      {/* Left panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, background: 'linear-gradient(135deg,#0a0f1e 0%,#1a2650 100%)' }} className="mobile-hide">
        <Building2 size={56} color="var(--brand-light)" style={{ marginBottom: 24 }} />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 40, color: '#fff', textAlign: 'center', lineHeight: 1.2, marginBottom: 16 }}>PropManager</h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 320, lineHeight: 1.7, fontSize: 15 }}>
          Property Management Application — properties, leases, payments, work orders and more.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 40, width: '100%', maxWidth: 340 }}>
          {[['Properties','Manage your full portfolio'],['Payments','Rent & receipt tracking'],['Work Orders','Maintenance workflow'],['Leases','Full lease lifecycle']].map(([t,d]) => (
            <div key={t} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-light)', marginBottom: 4 }}>{t}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, background: 'var(--white)' }}>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ marginBottom: 36 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={20} color="#fff" />
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>PropManager</span>
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{t('welcome')}</h2>
            <p style={{ color: 'var(--ink-40)', fontSize: 14 }}>{t('login')}</p>
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 20 }}>
              <AlertCircle size={16} />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">{t('usernameOrEmail') || 'Username or Email'}</label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type="text" required placeholder="mercuryA1  or  admin@company.com"
                  value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value.toLowerCase().trim() }))}
                  style={{ paddingLeft: 38 }} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('password')}</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} color="var(--ink-20)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input className="form-input" type={show ? 'text' : 'password'} required placeholder="••••••••"
                  value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  style={{ paddingLeft: 38, paddingRight: 40 }} />
                <button type="button" onClick={() => setShow(s => !s)}
                  style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-40)', padding: '0 12px', height: '100%', display: 'flex', alignItems: 'center', minWidth: 44 }}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button className="btn btn-primary w-full" type="submit" disabled={loading}
              style={{ justifyContent: 'center', padding: '12px', marginTop: 4, fontSize: 15 }}>
              {loading ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : t('loginButton')}
            </button>
          </form>

          {/* Register link */}
          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 14, color: 'var(--ink-40)' }}>
            {t('noAccount')}{' '}
            <Link to="/register" style={{ color: 'var(--brand)', fontWeight: 600 }}>{t('register')}</Link>
          </p>

          {/* Language switcher */}
          <div style={{ textAlign: 'center', marginTop: 14, display: 'flex', justifyContent: 'center', gap: 12 }}>
            {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
              <button key={code} onClick={() => i18n.changeLanguage(code)}
                style={{ fontSize: 12, color: i18n.language === code ? 'var(--brand)' : 'var(--ink-20)', fontWeight: i18n.language === code ? 700 : 400, background: 'none', border: 'none', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 28, fontSize: 11, color: 'var(--ink-20)', letterSpacing: '.04em' }}>
            Developed by{' '}
            <a href="https://mercurysoftwaresolutions.com/" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--ink-40)', textDecoration: 'none' }}
              onMouseEnter={e => e.target.style.textDecoration = 'underline'}
              onMouseLeave={e => e.target.style.textDecoration = 'none'}>
              Mercury Software Solutions
            </a>
            {' '}@2026
          </div>
        </div>
      </div>
    </div>
  );
}
